import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faLayerGroup,
  faMagnifyingGlass,
  faPlus,
  faRotateRight,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SkeletonTable } from '@/components/skeletons/SkeletonTable';
import {
  CategoryBadge,
  ChannelBadge,
  StatusBadge,
} from '@/components/communication/TemplateBadges';
import { CATEGORY_LABELS } from '@/components/communication/template-category.constants';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import { cn } from '@/lib/cn';
import {
  listMasters,
  listVariables,
  type EmailTemplateMaster,
  type VariableDefinition,
} from './api/platform-email-template.api';
import { TemplateDetailDrawer } from './components/TemplateDetailDrawer';
import { NewTemplateDialog } from './components/NewTemplateDialog';

const PAGE_SIZE = 25;

const SELECT_CLASS =
  'flex h-9 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

type SortKey = 'name' | 'category' | 'currentVersion' | 'status' | 'updatedAt';
type SortDir = 'asc' | 'desc';

function formatRelative(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * Platform Console — Email Template Library (COMMUNICATION_DESIGN.md §5).
 *
 * Columns are exactly §5.2. Usage / Last Published / Updated By are deliberately
 * absent from the list (§4.4 — they would need per-row requests or would render
 * UUIDs); they appear in the detail drawer instead.
 */
export function EmailTemplateLibrary() {
  const platformRole = usePlatformAuthStore((s) => s.user?.role);
  const canManage = useMemo(
    () => canPlatform(platformRole, 'platform:templates:manage'),
    [platformRole],
  );

  const [templates, setTemplates] = useState<EmailTemplateMaster[]>([]);
  const [variables, setVariables] = useState<VariableDefinition[]>([]);
  const [variablesLoading, setVariablesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Sort + pagination
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);

  // Selection
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listMasters({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(status ? { status } : {}),
        ...(category ? { category } : {}),
        isArchived: showArchived,
      });
      setTemplates(rows);
    } catch {
      setError('Could not load email templates. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [search, status, category, showArchived]);

  // Debounce search; other filters apply immediately.
  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => {
    void (async () => {
      try {
        setVariables(await listVariables());
      } catch {
        setVariables([]);
      } finally {
        setVariablesLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, category, status, showArchived]);

  // Category counts drive the sidebar; computed from the unfiltered-by-category set
  // so switching category does not make the other counts vanish.
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of templates) {
      counts.set(t.category, (counts.get(t.category) ?? 0) + 1);
    }
    return counts;
  }, [templates]);

  const sorted = useMemo(() => {
    const rows = [...templates];
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'currentVersion') cmp = a.currentVersion - b.currentVersion;
      else if (sortKey === 'updatedAt') cmp = a.updatedAt.localeCompare(b.updatedAt);
      else cmp = String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [templates, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const hasFilters = !!(search || category || status || showArchived);
  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setStatus('');
    setShowArchived(false);
  };

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  const SortHeader = ({ label, sortKey: key }: { label: string; sortKey: SortKey }) => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      className="flex items-center gap-1 transition-colors hover:text-foreground"
      aria-label={`Sort by ${label}`}
    >
      {label}
      {sortKey === key && (
        <span aria-hidden className="text-[10px]">
          {sortDir === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates…"
            aria-label="Search templates"
            className="pl-9"
          />
        </div>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
          className={SELECT_CLASS}
        >
          <option value="">All statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="ACTIVE">Active</option>
          <option value="DISABLED">Disabled</option>
        </select>

        <label className="flex items-center gap-2 whitespace-nowrap px-1 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-input"
          />
          Archived
        </label>

        <Button variant="secondary" size="sm" onClick={() => void load()} title="Refresh">
          <FontAwesomeIcon icon={faRotateRight} className="h-3.5 w-3.5" />
        </Button>

        {canManage && (
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3.5 w-3.5" />
            New Template
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Category sidebar — collapses to a dropdown on mobile (§5.6) */}
        <aside className="lg:w-52 lg:shrink-0">
          <div className="lg:hidden">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Filter by category"
              className={cn(SELECT_CLASS, 'w-full')}
            >
              <option value="">All categories</option>
              {Object.keys(CATEGORY_LABELS).map((key) => (
                <option key={key} value={key}>
                  {CATEGORY_LABELS[key]}
                </option>
              ))}
            </select>
          </div>

          <nav className="hidden rounded-xl border border-border bg-card p-2 lg:block">
            <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Categories
            </p>
            <button
              type="button"
              onClick={() => setCategory('')}
              className={cn(
                'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                category === '' ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
              )}
            >
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" />
                All
              </span>
              <span className="text-xs text-muted-foreground">{templates.length}</span>
            </button>
            {Object.keys(CATEGORY_LABELS).map((key) => {
              const count = categoryCounts.get(key) ?? 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(key)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                    category === key ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                    count === 0 && category !== key && 'opacity-50',
                  )}
                >
                  <span className="truncate">{CATEGORY_LABELS[key]}</span>
                  <span className="text-xs text-muted-foreground">{count}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Table */}
        <section className="min-w-0 flex-1">
          {loading ? (
            <SkeletonTable rows={8} cols={6} />
          ) : error ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/30 bg-destructive/5 py-16 text-center">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-6 w-6 text-destructive" />
              <p className="mt-3 text-sm text-foreground">{error}</p>
              <Button variant="secondary" size="sm" className="mt-4" onClick={() => void load()}>
                Retry
              </Button>
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-muted-foreground" />
              </div>
              {hasFilters ? (
                <>
                  <p className="mt-3 text-sm font-medium text-foreground">
                    No templates match these filters
                  </p>
                  <Button variant="secondary" size="sm" className="mt-4" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  <p className="mt-3 text-sm font-medium text-foreground">No email templates yet</p>
                  <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                    {canManage
                      ? 'Create a master template to distribute it to every tenant.'
                      : 'Templates will appear here once the platform team creates them.'}
                  </p>
                  {canManage && (
                    <Button size="sm" className="mt-4" onClick={() => setNewOpen(true)}>
                      <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3.5 w-3.5" />
                      New Template
                    </Button>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2.5 font-medium">
                        <SortHeader label="Template" sortKey="name" />
                      </th>
                      <th className="px-4 py-2.5 font-medium">
                        <SortHeader label="Category" sortKey="category" />
                      </th>
                      <th className="px-4 py-2.5 font-medium">Channel</th>
                      <th className="px-4 py-2.5 font-medium">
                        <SortHeader label="Version" sortKey="currentVersion" />
                      </th>
                      <th className="px-4 py-2.5 font-medium">
                        <SortHeader label="Status" sortKey="status" />
                      </th>
                      <th className="px-4 py-2.5 font-medium">
                        <SortHeader label="Last Modified" sortKey="updatedAt" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {pageRows.map((t) => (
                      <tr
                        key={t.id}
                        onClick={() => setSelectedId(t.id)}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-foreground">{t.name}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">
                            {t.templateKey}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <CategoryBadge category={t.category} />
                        </td>
                        <td className="px-4 py-2.5">
                          <ChannelBadge channel={t.channelType} />
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                          v{t.currentVersion}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={t.status} isArchived={t.isArchived} />
                        </td>
                        <td
                          className="px-4 py-2.5 text-xs text-muted-foreground"
                          title={new Date(t.updatedAt).toLocaleString()}
                        >
                          {formatRelative(t.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards (§5.6) */}
              <div className="space-y-2 md:hidden">
                {pageRows.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className="flex w-full flex-col gap-2 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted/40"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">{t.name}</div>
                        <div className="truncate font-mono text-[11px] text-muted-foreground">
                          {t.templateKey}
                        </div>
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowUpRightFromSquare}
                        className="mt-1 h-3 w-3 shrink-0 text-muted-foreground"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <CategoryBadge category={t.category} />
                      <StatusBadge status={t.status} isArchived={t.isArchived} />
                      <span className="font-mono text-[11px] text-muted-foreground">
                        v{t.currentVersion}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination (client-side — §4.5) */}
              {totalPages > 1 && (
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of{' '}
                    {sorted.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <TemplateDetailDrawer
        template={selected}
        variables={variables}
        variablesLoading={variablesLoading}
        canManage={canManage}
        onClose={() => setSelectedId(null)}
        onChanged={() => void load()}
      />

      <NewTemplateDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(created) => {
          setNewOpen(false);
          void load();
          setSelectedId(created.id);
        }}
      />
    </div>
  );
}
